import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, Button, Card, message, Tooltip } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { RadAIService } from "../../services/radai-service";
import SpeechRecognitionComponent from './SpeechRecognitionComponent';
import { X as CloseIcon, Copy as CopyIcon, Check as CheckIcon, Volume2 as SpeakIcon, Square as StopIcon, Reply as ReplyIcon, HeartHandshake as PatientIcon } from 'lucide-react';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

const turndownService = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' });
turndownService.use(gfm);

const { TextArea } = Input;

// CSS for animations and scrollbar
const style = document.createElement('style');
style.textContent = `
  .claire-chat-container::-webkit-scrollbar {
    display: none;
  }

  .claire-chat-input::-webkit-scrollbar {
    display: none;
  }

  .claire-rich-input:empty:before {
    content: attr(data-placeholder);
    color: #bfbfbf;
    pointer-events: none;
  }

  .claire-rich-input table {
    border-collapse: collapse;
    margin: 6px 0;
    font-size: 13px;
  }

  .claire-rich-input th,
  .claire-rich-input td {
    border: 1px solid #d9d9d9;
    padding: 4px 8px;
    text-align: left;
  }

  /* Thin, subtle horizontal scrollbar for wide tables in chat bubbles
     (overrides the app's chunky global scrollbar styling). */
  .claire-table-scroll {
    scrollbar-width: thin;               /* Firefox */
    scrollbar-color: #c9c9c9 transparent;
  }
  .claire-table-scroll::-webkit-scrollbar {
    height: 4px;
    width: 4px;
    background: transparent;
  }
  .claire-table-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .claire-table-scroll::-webkit-scrollbar-thumb {
    background: #c9c9c9;
    border-radius: 2px;
  }

  /* Chat tables: defend against global app CSS (table-layout, word-break,
     display overrides) that squeezes columns until letters stack vertically. */
  .claire-table-scroll table {
    table-layout: auto !important;
    display: table !important;
    width: 100% !important;
  }
  .claire-table-scroll th,
  .claire-table-scroll td {
    word-break: normal !important;
    overflow-wrap: normal !important;
    white-space: normal !important;
    hyphens: none !important;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 80%, 100% { 
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% { 
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }

  /* Modern minimal status filler: a soft light sweep across the text
     (gradient clipped to the glyphs), no italics, no scaling. */
  .claire-status-shimmer {
    display: inline-block;
    font-size: 13px;
    font-weight: 500;
    background: linear-gradient(90deg, #9aa0a6 30%, #3c4043 50%, #9aa0a6 70%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
    animation: claire-shimmer 1.8s linear infinite;
  }
  @keyframes claire-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* Radiology quick-prompt marquee above the input: auto-scrolls slowly,
     pauses on hover, chips insert their text into the input. */
  .claire-shortcuts {
    overflow: hidden;
    margin: 0 8px 8px 8px;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent);
    mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent);
  }
  .claire-shortcuts:hover .claire-shortcuts-track {
    animation-play-state: paused;
  }
  .claire-shortcuts-track {
    display: flex;
    width: max-content;
    animation: claire-marquee 45s linear infinite;
  }
  @keyframes claire-marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .claire-shortcut-chip {
    flex-shrink: 0;
    margin-right: 8px;
    border: 1px solid #e8e8e8;
    background: #fafafa;
    color: #595959;
    border-radius: 999px;
    padding: 4px 12px;
    font-size: 12px;
    line-height: 1.4;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;
  }
  .claire-shortcut-chip:hover {
    background: #efefef;
    color: #262626;
    border-color: #d9d9d9;
  }

  /* Solid black tooltips for chat action buttons */
  .claire-tooltip .ant-tooltip-inner {
    background: #000000;
    color: #ffffff;
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.3;
    min-height: 0;
    padding: 4px 10px;
  }
  /* Hide the arrow - it reads as an odd gap under the label */
  .claire-tooltip .ant-tooltip-arrow {
    display: none;
  }
`;
document.head.appendChild(style);

const getMessageNode = (messageId) =>
  document.querySelector(`[data-claire-message-id="${messageId}"]`);

const messageActionButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '6px',
  color: '#8c8c8c',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

// Custom markdown styles for AI responses (module scope: stable identity)
const markdownStyles = {
  h1: { fontSize: '16px', fontWeight: 'bold', margin: '12px 0 6px 0', color: '#1a1a1a' },
  h2: { fontSize: '15px', fontWeight: 'bold', margin: '10px 0 5px 0', color: '#1a1a1a' },
  h3: { fontSize: '14px', fontWeight: 'bold', margin: '8px 0 4px 0', color: '#1a1a1a' },
  h4: { fontSize: '13px', fontWeight: 'bold', margin: '6px 0 3px 0', color: '#1a1a1a' },
  p: { margin: '6px 0', lineHeight: '1.6', fontSize: '13px' },
  ul: { margin: '6px 0', paddingLeft: '18px' },
  ol: { margin: '6px 0', paddingLeft: '18px' },
  li: { margin: '1px 0', lineHeight: '1.4' },
  strong: { fontWeight: 'bold', color: '#1a1a1a' },
  em: { fontStyle: 'italic' },
  code: {
    backgroundColor: 'rgba(0,0,0,0.1)',
    padding: '1px 3px',
    borderRadius: '2px',
    fontSize: '12px',
    fontFamily: 'monospace'
  },
  blockquote: {
    borderLeft: '2px solid #ddd',
    paddingLeft: '8px',
    margin: '6px 0',
    fontStyle: 'italic',
    color: '#666'
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    margin: '8px 0',
    fontSize: '12px'
  },
  th: {
    border: '1px solid #ddd',
    padding: '6px 8px',
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    textAlign: 'left',
    wordBreak: 'normal',
    overflowWrap: 'normal'
  },
  td: {
    border: '1px solid #ddd',
    padding: '6px 8px',
    textAlign: 'left',
    wordBreak: 'normal',
    overflowWrap: 'normal'
  }
};

const markdownComponents = {
  h1: ({node, ...props}) => <h1 style={markdownStyles.h1} {...props} />,
  h2: ({node, ...props}) => <h2 style={markdownStyles.h2} {...props} />,
  h3: ({node, ...props}) => <h3 style={markdownStyles.h3} {...props} />,
  h4: ({node, ...props}) => <h4 style={markdownStyles.h4} {...props} />,
  p: ({node, ...props}) => <p style={markdownStyles.p} {...props} />,
  ul: ({node, ...props}) => <ul style={markdownStyles.ul} {...props} />,
  ol: ({node, ...props}) => <ol style={markdownStyles.ol} {...props} />,
  li: ({node, ...props}) => <li style={markdownStyles.li} {...props} />,
  strong: ({node, ...props}) => <strong style={markdownStyles.strong} {...props} />,
  em: ({node, ...props}) => <em style={markdownStyles.em} {...props} />,
  code: ({node, ...props}) => <code style={markdownStyles.code} {...props} />,
  // Links always open in a new tab - navigating the RIS away mid-report would lose work.
  a: ({node, ...props}) => <a style={{ color: '#1890ff', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" {...props} />,
  // Slim divider: the app's global hr style renders as a tall rounded bar.
  hr: ({node, ...props}) => <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', height: 0, margin: '10px 0', background: 'none' }} {...props} />,
  blockquote: ({node, ...props}) => <blockquote style={markdownStyles.blockquote} {...props} />,
  table: ({node, ...props}) => (
    <div className="claire-table-scroll" style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <table style={markdownStyles.table} {...props} />
    </div>
  ),
  th: ({node, ...props}) => <th style={markdownStyles.th} {...props} />,
  td: ({node, ...props}) => <td style={markdownStyles.td} {...props} />
};

export { markdownComponents, userMarkdownComponents };

export const htmlToMarkdown = (html) => turndownService.turndown(html || '');

export const RADIOLOGY_SHORTCUTS = [
  'Report template for ',
  'MBS item and fee for ',
  'Differential diagnosis for ',
  'Normal measurements for ',
  'RANZCR guideline on ',
  'Contrast protocol for ',
  'Follow-up recommendation for ',
  'Staging summary for ',
  'Explain to a patient: ',
  'Radiation dose for '
];

export const ChatShortcuts = ({ onPick, disabled }) => (
  <div className="claire-shortcuts" aria-label="Radiology quick prompts">
    <div className="claire-shortcuts-track">
      {[...RADIOLOGY_SHORTCUTS, ...RADIOLOGY_SHORTCUTS].map((text, i) => (
        <button
          key={i}
          type="button"
          className="claire-shortcut-chip"
          tabIndex={i < RADIOLOGY_SHORTCUTS.length ? 0 : -1}
          disabled={disabled}
          onClick={() => onPick && onPick(text)}
        >
          {text.trim().replace(/:$/, '')}
        </button>
      ))}
    </div>
  </div>
);

const userMarkdownComponents = {
  h1: ({node, ...props}) => <h1 style={{ fontSize: '15px', fontWeight: 'bold', margin: '8px 0 4px 0', color: 'inherit' }} {...props} />,
  h2: ({node, ...props}) => <h2 style={{ fontSize: '14px', fontWeight: 'bold', margin: '8px 0 4px 0', color: 'inherit' }} {...props} />,
  h3: ({node, ...props}) => <h3 style={{ fontSize: '14px', fontWeight: 'bold', margin: '6px 0 3px 0', color: 'inherit' }} {...props} />,
  h4: ({node, ...props}) => <h4 style={{ fontSize: '13px', fontWeight: 'bold', margin: '6px 0 3px 0', color: 'inherit' }} {...props} />,
  p: ({node, ...props}) => <p style={{ margin: '4px 0', lineHeight: '1.5', color: 'inherit' }} {...props} />,
  ul: ({node, ...props}) => <ul style={{ margin: '4px 0', paddingLeft: '18px', color: 'inherit' }} {...props} />,
  ol: ({node, ...props}) => <ol style={{ margin: '4px 0', paddingLeft: '18px', color: 'inherit' }} {...props} />,
  li: ({node, ...props}) => <li style={{ margin: '1px 0', lineHeight: '1.4', color: 'inherit' }} {...props} />,
  strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold', color: 'inherit' }} {...props} />,
  em: ({node, ...props}) => <em style={{ fontStyle: 'italic', color: 'inherit' }} {...props} />,
  code: ({node, ...props}) => <code style={{ backgroundColor: 'rgba(255,255,255,0.15)', padding: '1px 3px', borderRadius: '2px', fontSize: '12px', fontFamily: 'monospace', color: 'inherit' }} {...props} />,
  a: ({node, ...props}) => <a style={{ color: '#91d5ff', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" {...props} />,
  hr: ({node, ...props}) => <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.35)', height: 0, margin: '8px 0', background: 'none' }} {...props} />,
  table: ({node, ...props}) => (
    <div className="claire-table-scroll" style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', margin: '6px 0', fontSize: '12px' }} {...props} />
    </div>
  ),
  th: ({node, ...props}) => <th style={{ border: '1px solid rgba(255,255,255,0.4)', padding: '4px 6px', backgroundColor: 'rgba(255,255,255,0.1)', fontWeight: 'bold', textAlign: 'left', color: 'inherit', wordBreak: 'normal', overflowWrap: 'normal' }} {...props} />,
  td: ({node, ...props}) => <td style={{ border: '1px solid rgba(255,255,255,0.4)', padding: '4px 6px', textAlign: 'left', color: 'inherit', wordBreak: 'normal', overflowWrap: 'normal' }} {...props} />
};

const MessageContent = React.memo(({ message, liveContent, streamingRef, isCopied, isSpeaking, onCopy, onReadAloud, onReply, onPatientFriendly, statusText }) => {
  const baseMessageStyle = {
    maxWidth: '85%',
    padding: '12px 16px',
    borderRadius: '16px',
    fontSize: '14px',
    lineHeight: '1.4',
    wordBreak: 'break-word'
  };

  const messageStyle = {
    ...baseMessageStyle,
    backgroundColor: message.isError
      ? '#ffebee'
      : message.role === 'user' ? '#2c2c2c' : '#f0f0f0',
    color: message.isError
      ? '#c62828'
      : message.role === 'user' ? 'white' : '#262626',
    border: message.isError ? '1px solid #ffcdd2' : 'none'
  };

  if (message.role === 'assistant' && !message.isError) {
    // For streaming messages, use live content; for completed messages, use stored content
    const contentToRender = liveContent != null ? liveContent : message.content;

    // Waiting for the first token: bare shimmer status, no bubble.
    if (message.isStreaming && (!contentToRender || contentToRender.length === 0)) {
      return (
        <div style={{ padding: '6px 8px' }}>
          <span className="claire-status-shimmer">{statusText || 'Thinking...'}</span>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '85%' }}>
      <div style={{ ...messageStyle, maxWidth: '100%' }}>
        <div
          data-claire-message-id={message.id}
          style={{ position: 'relative', display: 'inline-block', width: '100%' }}
          ref={message.isStreaming ? streamingRef : null}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {contentToRender}
          </ReactMarkdown>
          {message.isStreaming && contentToRender && contentToRender.length > 0 && (
            <span style={{
              display: 'inline',
              color: '#333',
              animation: 'blink 1s infinite',
              fontSize: '14px',
              lineHeight: '1.4'
            }}>|</span>
          )}
        </div>
      </div>
      {!message.isStreaming && (
        <div style={{ display: 'flex', gap: '2px', marginTop: '2px', paddingLeft: '6px' }}>
          <Tooltip title={isCopied ? 'Copied!' : 'Copy'} mouseEnterDelay={0.2} placement="bottom" overlayClassName="claire-tooltip">
            <button
              type="button"
              onClick={() => onCopy(message)}
              aria-label="Copy message"
              style={messageActionButtonStyle}
            >
              {isCopied
                ? <CheckIcon size={14} color="#28a745" />
                : <CopyIcon size={14} />}
            </button>
          </Tooltip>
          <Tooltip title={isSpeaking ? 'Stop reading' : 'Read aloud'} mouseEnterDelay={0.2} placement="bottom" overlayClassName="claire-tooltip">
            <button
              type="button"
              onClick={() => onReadAloud(message)}
              aria-label={isSpeaking ? 'Stop reading' : 'Read message aloud'}
              style={{
                ...messageActionButtonStyle,
                color: isSpeaking ? '#1890ff' : '#8c8c8c'
              }}
            >
              {isSpeaking
                ? <StopIcon size={14} />
                : <SpeakIcon size={14} />}
            </button>
          </Tooltip>
          <Tooltip title="Reply to this message" mouseEnterDelay={0.2} placement="bottom" overlayClassName="claire-tooltip">
            <button
              type="button"
              onClick={() => onReply && onReply(message)}
              aria-label="Reply to this message"
              style={messageActionButtonStyle}
            >
              <ReplyIcon size={14} />
            </button>
          </Tooltip>
          <Tooltip title="Patient-friendly rewrite" mouseEnterDelay={0.2} placement="bottom" overlayClassName="claire-tooltip">
            <button
              type="button"
              onClick={() => onPatientFriendly && onPatientFriendly(message)}
              aria-label="Rewrite in patient-friendly language"
              style={messageActionButtonStyle}
            >
              <PatientIcon size={14} />
            </button>
          </Tooltip>
        </div>
      )}
      </div>
    );
  }

  // User messages are markdown too (the rich input converts to markdown on
  // send), rendered with a light-on-dark component set so pasted tables and
  // formatting look right inside the dark bubble.
  if (message.role === 'user' && !message.isError) {
    return (
      <div style={messageStyle}>
        {message.replyPreview && (
          <div style={{
            borderLeft: '3px solid rgba(255,255,255,0.45)',
            paddingLeft: '8px',
            marginBottom: '6px',
            fontSize: '12px',
            opacity: 0.75,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {message.replyPreview}
          </div>
        )}
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={userMarkdownComponents}>
          {message.content}
        </ReactMarkdown>
      </div>
    );
  }

  // Error messages: plain text, pre-wrap preserves newlines.
  return (
    <div style={{ ...messageStyle, whiteSpace: 'pre-wrap' }}>
      {message.content}
    </div>
  );
});

const ClaireChatPopup = ({ isOpen, onClose, sidebarCollapsed = false }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: "assistant",
      content: "Hi! I'm Claire, your ClarityAI assistant. How can I help you today?",
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatSession, setChatSession] = useState(null);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);

  const [streamingStatus, setStreamingStatus] = useState(null);
  const [replyTo, setReplyTo] = useState(null);

  const handleReplyTo = useCallback((msg) => {
    setReplyTo({
      id: msg.id,
      content: msg.content,
      preview: RadAIService.markdownToPlainText(msg.content).replace(/\s+/g, ' ').slice(0, 120)
    });
    if (inputRef.current) inputRef.current.focus();
  }, []);
  const messagesEndRef = useRef(null);
  const streamingRef = useRef(null);
  const chatContainerRef = useRef(null);
  const nearBottomRef = useRef(true);
  const inputRef = useRef(null);
  const [resetTranscriptFn, setResetTranscriptFn] = useState(null);
  const INPUT_MAX_HEIGHT = 112;

  const scrollToBottom = () => {
    // Instant scroll via scrollTop: smooth scrollIntoView animations lag
    // behind a fast stream and get cancelled/restarted by each new chunk.
    const el = chatContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView();
    }
  };

  const handleChatScroll = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      nearBottomRef.current = true;
    }
  };

  const handleChatWheel = (e) => {
    if (e.deltaY < 0) {
      nearBottomRef.current = false; // user is scrolling up on purpose
    }
  };

  const touchStartYRef = useRef(0);
  const handleChatTouchStart = (e) => {
    touchStartYRef.current = e.touches && e.touches.length ? e.touches[0].clientY : 0;
  };
  const handleChatTouchMove = (e) => {
    const y = e.touches && e.touches.length ? e.touches[0].clientY : 0;
    if (y > touchStartYRef.current + 5) {
      nearBottomRef.current = false; // finger dragging down = scrolling up
    }
  };

  const followAnimRef = useRef(null);

  const startFollowLoop = () => {
    if (followAnimRef.current) return; // already chasing
    const step = () => {
      followAnimRef.current = null;
      const el = chatContainerRef.current;
      if (!el || !nearBottomRef.current) return;
      const target = el.scrollHeight - el.clientHeight;
      const delta = target - el.scrollTop;
      if (delta > 1) {
        el.scrollTop = el.scrollTop + Math.max(1, delta * 0.2);
        followAnimRef.current = requestAnimationFrame(step);
      }
    };
    followAnimRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    if (nearBottomRef.current) {
      startFollowLoop();
    }
  }, [messages, streamingContent]);

  useEffect(() => () => {
    if (followAnimRef.current) cancelAnimationFrame(followAnimRef.current);
  }, []);

  // Handle ESC key to close
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  // Stop any read-aloud when the popup closes or unmounts
  useEffect(() => {
    if (!isOpen && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeakingMessageId(null);
    }
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [isOpen]);

  const handleCopyMessage = useCallback(async (msg) => {
    const node = getMessageNode(msg.id);
    const html = node ? node.innerHTML : msg.content;
    const plain = msg.content
      ? RadAIService.markdownToPlainText(msg.content)
      : (node ? node.innerText : '');
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plain], { type: 'text/plain' })
          })
        ]);
      } else if (node) {
        // Fallback: select the rendered node and copy — still rich text
        const range = document.createRange();
        range.selectNodeContents(node);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
      } else {
        await navigator.clipboard.writeText(plain);
      }
      setCopiedMessageId(msg.id);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (e) {
      console.error('[Claire] Copy failed', e);
      message.error('Could not copy message');
    }
  }, []);

  const handleReadAloud = useCallback((msg) => {
    if (!window.speechSynthesis) {
      message.error('Read aloud is not supported in this browser');
      return;
    }
    // Toggle off if this message is already being read
    if (speakingMessageId === msg.id) {
      window.speechSynthesis.cancel();
      setSpeakingMessageId(null);
      return;
    }
    window.speechSynthesis.cancel();
    const node = getMessageNode(msg.id);
    const text = node ? node.innerText : msg.content;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.onend = () => setSpeakingMessageId(null);
    utterance.onerror = () => setSpeakingMessageId(null);
    setSpeakingMessageId(msg.id);
    window.speechSynthesis.speak(utterance);
  }, [speakingMessageId]);

  // Quick-prompt chip: insert text into the rich input, caret at the end.
  const handleShortcutPick = (text) => {
    const el = inputRef.current;
    if (!el) return;
    const current = el.innerText.replace(/\u00a0/g, ' ').trim();
    el.innerText = current ? current + ' ' + text : text;
    setInputValue(el.innerText);
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const PATIENT_FRIENDLY_PROMPT =
    'Rewrite the above in clear, patient-friendly language: no jargon, short sentences, ' +
    'explain any necessary medical terms simply, keep every clinical fact accurate, and do ' +
    'not add reassurance or advice that is not in the original. Australian English.';

  // One-click quick action: patient-friendly rewrite of a specific message.
  const handlePatientFriendly = (msg) => {
    if (isLoading || streamingMessageId) return;
    sendChatMessage(PATIENT_FRIENDLY_PROMPT, {
      id: msg.id,
      content: msg.content,
      preview: 'Patient-friendly rewrite of: ' +
        RadAIService.markdownToPlainText(msg.content).replace(/\s+/g, ' ').slice(0, 90)
    });
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const el = inputRef.current;
    let userMessage;
    try {
      userMessage = el ? turndownService.turndown(el.innerHTML).trim() : inputValue.trim();
    } catch (convErr) {
      console.warn('[Claire] HTML->markdown conversion failed, sending plain text', convErr);
      userMessage = (el ? el.innerText : inputValue).trim();
    }
    if (!userMessage) return;

    setInputValue(''); // Clear input state after sending
    if (el) el.innerHTML = ''; // Clear the editor itself
    if (resetTranscriptFn) resetTranscriptFn(); // Reset transcript after sending

    const activeReply = replyTo; // capture before clearing
    setReplyTo(null);

    await sendChatMessage(userMessage, activeReply);
  };

  // Core send pipeline, also used by quick actions (reply / patient-friendly)
  // that construct their message programmatically instead of via the input.
  const sendChatMessage = async (userMessage, activeReply) => {
    setIsLoading(true);

    const newUserMessage = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      replyPreview: activeReply ? activeReply.preview : null,
      timestamp: new Date().toISOString()
    };
    // Sending re-engages stick-to-bottom so the user sees their message
    // and the start of the reply, even if they had scrolled up.
    nearBottomRef.current = true;
    setMessages(prev => [...prev, newUserMessage]);
    
    try {
      const currentSession = chatSession || RadAIService.initializeClaireChat();
      if (!chatSession) setChatSession(currentSession);
      
      // Add user message to session
      RadAIService.addMessage(currentSession, userMessage, 'user');
      
      // Prepare messages for API: full conversation history (user AND
      // assistant turns) so the model has context for follow-up questions,
      // trimmed to the most recent maxMessages to bound token usage.
      const maxHistory =
        (RadAIService.getConfig().claire && RadAIService.getConfig().claire.maxMessages) || 50;
      // Reply mode: send ONLY the quoted AI message + the new question, so
      // the user can branch off any earlier answer without the rest of the
      // conversation diluting it. Otherwise: normal trimmed history.
      const apiMessages = activeReply
        ? [
            { role: 'assistant', content: activeReply.content },
            { role: 'user', content: userMessage }
          ]
        : currentSession.messages
            .filter(msg => msg.role !== 'system') // backend owns the system prompt
            .slice(-maxHistory)
            .map(msg => ({
              role: msg.role,
              content: msg.content
            }));
      
      // Create AI message placeholder for streaming
      const aiMessageId = Date.now() + 1;
      const aiMessage = { 
        id: aiMessageId, 
        role: 'assistant', 
        content: '', 
        timestamp: new Date().toISOString(),
        isStreaming: true
      };
      
      setMessages(prev => [...prev, aiMessage]);
      setStreamingMessageId(aiMessageId);
      setStreamingStatus('Thinking...');
      
      // Performance optimization: Use throttled updates to reduce re-renders
      let accumulatedContent = '';
      let updateTimeout = null;
      
      const throttledUpdate = () => {
        setStreamingContent(accumulatedContent);
        updateTimeout = null;
      };
      
      // Call Claire Chat Streaming API
      await RadAIService.callClaireStream(
        apiMessages,
        // onChunk - called for each piece of content (real-time streaming)
        (chunk) => {
          if (accumulatedContent === '') {
            setStreamingStatus(null); // first token: retire the filler
          }
          accumulatedContent += chunk;

          setStreamingContent(accumulatedContent);

          if (!updateTimeout) {
            updateTimeout = setTimeout(() => {
              updateTimeout = null;
            }, 16); // ~60fps for smooth scrolling
          }
        },
        // onComplete - called when streaming is done
        () => {
          // Clear any pending throttled update
          if (updateTimeout) {
            clearTimeout(updateTimeout);
            updateTimeout = null;
          }
          
          // Use the streamed content verbatim. The old formatMedicalReport()
          // rewrite pass was a repair kit for the days when SSE transport
          // corrupted the text; on clean markdown it CAUSED damage (e.g. its
          // '---' rule exploded GFM table separator rows into horizontal
          // rules). remark-gfm renders the model's markdown correctly as-is.
          const formattedContent = accumulatedContent;

          // Record the assistant's reply in the session so the NEXT request
          // carries it as history. Without this, only user messages were ever
          // sent and the model could not see its own previous answers -
          // follow-ups like "refine it" had nothing to refine.
          RadAIService.addMessage(currentSession, formattedContent, 'assistant');

          // Final update with complete content
          setStreamingContent(formattedContent);
          
          // Update the actual message with final content and mark as complete
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: formattedContent, isStreaming: false }
              : msg
          ));
          
          setStreamingMessageId(null);
          setStreamingContent('');
          setStreamingStatus(null);
          setIsLoading(false);
        },
        // onError - called if streaming fails
        (error) => {
          console.error('[Claire] ChatGPT Streaming Error:', error);
          
          // Clear any pending updates
          if (updateTimeout) {
            clearTimeout(updateTimeout);
            updateTimeout = null;
          }
          
          // Replace streaming message with error
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? {
                  ...msg,
                  content: `I apologize, but I'm having trouble connecting right now. ${error.message.includes('API key') ? 'Please contact your administrator to configure the ClarityAI API.' : 'Please try again in a moment.'}`,
                  isError: true,
                  isStreaming: false
                }
              : msg
          ));
          
          setStreamingMessageId(null);
          setStreamingContent('');
          setStreamingStatus(null);
          setIsLoading(false);
          message.error('Unable to get response from Claire. Please try again.');
        },
        // onStatus - progress fillers while waiting for the first token
        (status) => setStreamingStatus(status)
      );
      
    } catch (error) {
      console.error('[Claire] ChatGPT API Error:', error);
      
      // Show error message to user
      const errorMessage = { 
        id: Date.now() + 1, 
        role: 'assistant', 
        content: `I apologize, but I'm having trouble connecting right now. ${error.message.includes('API key') ? 'Please contact your administrator to configure the ClarityAI API.' : 'Please try again in a moment.'}`, 
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      
      // Show user-friendly error message
      message.error('Unable to get response from Claire. Please try again.');
    }
  };

  const handleChatClose = () => {
    onClose();
    setInputValue(''); // Clear input when chat is closed
    if (resetTranscriptFn) resetTranscriptFn(); // Reset transcript when chat is closed
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTranscript = (transcript) => {
    if (transcript && transcript.trim()) {
      setInputValue(transcript);
      // The contenteditable is uncontrolled - mirror the transcript into it.
      const el = inputRef.current;
      if (el && el.innerText !== transcript) {
        el.innerText = transcript;
      }
    }
  };

  const isInputDisabled = isLoading || streamingMessageId;

  if (!isOpen) return null;

  return (
    <Card
      style={{
        position: "fixed",
        bottom: "20px",
        // Track the sidebar: mini sidebar is 80px wide (see
        // @sidebar-mini-drawer-width), full sidebar ~260px; +20px gap.
        left: sidebarCollapsed ? "100px" : "280px",
        transition: "left 0.25s ease",
        width: "500px",
        maxWidth: "calc(100vw - 120px)", // never overflow small screens
        height: "560px",
        zIndex: 999,
        display: "flex",
        flexDirection: "column",
        padding: 0,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: "16px",
        border: "1px solid #e8e8e8",
        overflow: "hidden",
        backgroundColor: "#fafafa"
      }}
      bodyStyle={{
        padding: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#ffffff"
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src="/Claire.png"
            alt="Claire"
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              marginRight: "12px"
            }}
          />
          <div>
            <div style={{ fontWeight: "600", fontSize: "16px", color: "#262626" }}>Claire</div>
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>ClarityAI Assistant</div>
          </div>
        </div>
        <Button
          type="text"
          onClick={handleChatClose}
          style={{
            color: "#000000",
            width: "32px",
            height: "32px",
            minWidth: "32px",
            padding: 0,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            border: "none"
          }}
        >
          <CloseIcon size={20} style={{ flexShrink: 0 }} />
        </Button>
      </div>

      {/* Messages */}
      <div
        className="claire-chat-container"
        ref={chatContainerRef}
        onScroll={handleChatScroll}
        onWheel={handleChatWheel}
        onTouchStart={handleChatTouchStart}
        onTouchMove={handleChatTouchMove}
        style={{
          flex: 1,
          padding: "16px",
          overflowY: "auto",
          backgroundColor: "#ffffff",
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none' // IE/Edge
        }}
      >
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              marginBottom: "16px",
              display: "flex",
              justifyContent: message.role === "user" ? "flex-end" : "flex-start"
            }}
          >
            <MessageContent
              message={message}
              liveContent={message.isStreaming && message.id === streamingMessageId ? streamingContent : null}
              streamingRef={message.isStreaming ? streamingRef : null}
              isCopied={copiedMessageId === message.id}
              isSpeaking={speakingMessageId === message.id}
              onCopy={handleCopyMessage}
              onReadAloud={handleReadAloud}
              onReply={handleReplyTo}
              onPatientFriendly={handlePatientFriendly}
              statusText={message.isStreaming && message.id === streamingMessageId ? streamingStatus : null}
            />
          </div>
        ))}
        
        {/* Loading indicator - only show when no streaming message */}
        {isLoading && !streamingMessageId && (
          <div style={{ 
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: '16px'
          }}>
            <div style={{
              maxWidth: '85%',
              padding: '12px 16px',
              borderRadius: '16px',
              backgroundColor: '#f0f0f0',
              color: '#262626',
              fontSize: '14px',
              lineHeight: '1.4',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                display: 'flex',
                gap: '4px'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#666',
                  animation: 'pulse 1.4s ease-in-out infinite both',
                  animationDelay: '0s'
                }} />
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#666',
                  animation: 'pulse 1.4s ease-in-out infinite both',
                  animationDelay: '0.2s'
                }} />
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#666',
                  animation: 'pulse 1.4s ease-in-out infinite both',
                  animationDelay: '0.4s'
                }} />
              </div>
              <span style={{ opacity: 0.7 }}>Claire is connecting...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          padding: "16px",
          backgroundColor: "#ffffff"
        }}
      >
        {replyTo && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '0 8px 8px 8px',
            padding: '6px 12px',
            background: '#f5f5f5',
            borderLeft: '3px solid #bfbfbf',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#595959'
          }}>
            <ReplyIcon size={13} style={{ flexShrink: 0, color: '#8c8c8c' }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {replyTo.preview}
            </span>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              aria-label="Cancel reply"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', color: '#8c8c8c' }}
            >
              <CloseIcon size={13} />
            </button>
          </div>
        )}
        {messages.length <= 1 && (
          <ChatShortcuts onPick={handleShortcutPick} disabled={isInputDisabled} />
        )}
        <div
          style={{
            background: "rgba(255, 255, 255, 0.7)",
            borderRadius: "28px",
            padding: "4px",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s ease"
          }}
        >
          {/* Rich text input: a contenteditable renders pasted formatting
              (tables, bold, headings) as-is; on send it is converted back
              to markdown for the model. Uncontrolled by design - React
              never rewrites its children, so the caret never jumps. */}
          <div
            ref={inputRef}
            className="claire-chat-input claire-rich-input"
            contentEditable={!isInputDisabled}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Ask Claire"
            data-placeholder={streamingMessageId ? "Claire is responding..." : isLoading ? "Claire is connecting..." : "Ask Claire"}
            onInput={(e) => setInputValue(e.currentTarget.innerText)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && !isInputDisabled && (e.preventDefault(), handleSendMessage())}
            style={{
              border: "none",
              borderRadius: "0",
              padding: "14px 18px",
              fontSize: "14px",
              outline: "none",
              fontFamily: "inherit",
              flex: 1,
              minHeight: "20px",
              maxHeight: `${INPUT_MAX_HEIGHT}px`,
              backgroundColor: "transparent",
              lineHeight: "1.5",
              overflowY: "auto", // grows with content, scrolls past the cap
              scrollbarWidth: "none", // Firefox: hide scrollbar, keep scrolling
              msOverflowStyle: "none", // IE/Edge legacy
              transition: "none",
              boxShadow: "none",
              color: "#000000",
              wordBreak: "break-word",
              opacity: isInputDisabled ? 0.6 : 1,
              cursor: isInputDisabled ? "not-allowed" : "text"
            }}
          />
          <SpeechRecognitionComponent onTranscript={handleTranscript} onResetTranscript={setResetTranscriptFn} />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isInputDisabled}
            style={{
              border: "none",
              background: isInputDisabled 
                ? "linear-gradient(135deg, #666666 0%, #444444 100%)" 
                : "linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%)",
              color: "white",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              cursor: (!inputValue.trim() || isInputDisabled) ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              flexShrink: 0,
              opacity: (!inputValue.trim() || isInputDisabled) ? 0.7 : 1
            }}
          >
            {isLoading ? (
              <div style={{
                width: "18px",
                height: "18px",
                border: "2px solid rgba(255,255,255,0.3)",
                borderRadius: "50%",
                borderTopColor: "white",
                animation: "spin 1s linear infinite"
              }} />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            )}
          </button>
        </div>
        {messages.length > 1 && (
          <div style={{
            fontSize: '11px',
            color: '#8c8c8c',
            textAlign: 'center',
            marginTop: '8px',
            opacity: 0.8
          }}>
            Claire is AI and can make mistakes. Please double-check responses.
          </div>
        )}
      </div>
    </Card>
  );
};

export default ClaireChatPopup; 